import React, { useEffect, useContext, useMemo, useState } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { Dashboard, ParamTypes } from "../../interfaces/Dashboard";
import { defineMessages, IntlShape, useIntl } from "react-intl";
import { sessionStore } from "../../contexts/SessionContext";
import SpecimenHelper from "../DashBoard/DashboardTeacher/SpecimenHelper/SpecimenHelper";
import PageWrapper from "../../components/PageWrapper/PageWrapper";
import ContainerBlock from "../../design-system-components/ContainerBlock/ContainerBlock";
import PageHeader from "../../components/PageHeader/PageHeader";
import ClusterDataTable from "./ClustersDataTable/ClustersDataTable";
import {
    AggregatedInfo,
    Cluster,
    GROUPS_MEAN_SCORE_ID,
    generateClustering,
    getClusterName,
} from "./utils";
import ClustersVisualRepresentation from "./ClustersVisualRepresentation/ClustersVisualRepresentation";
import ClusteringNav from "./ClusteringNav/ClusteringNav";
import "./Clustering.scss";
import Select from "../../components/Select/Select";
import dashboardMessages from "../DashBoard/DashboardTeacher/dashboardMessages";
import { errorStore } from "../../contexts/ErrorContext";
import useScrollTopOnLocationChange from "../../hooks/useScrollTopOnLocationChange ";
import TabTitle from "../../components/TabTitle/TabTitle";
import { useDashboardRouter } from "../DashBoard/DashboardTeacher/routing";
import { useImages } from "../../hooks/useConfig";
import { defaultImages } from "../../utils/defaultImages";
import EmptyContainer from "../../design-system-components/WorkmodeLayout/EmptyContainer/EmptyContainer";
import { LegacyLoader } from "../../components/Loader/Loader";
import { useDynamicDashboard } from "../../hooks/useDashboard";
interface Props {
    classrooms: Dashboard["classrooms"];
    clustering: Required<Dashboard>["clustering"];
}
const Clustering = ({ classrooms, clustering }: Props) => {
    const intl = useIntl();
    useScrollTopOnLocationChange();
    const { classroomId, moduleId } = useParams<ParamTypes>();
    const {
        progressionOverview: getProgressionOverviewUrl,
        classroomList: classroomListUrl,
    } = useDashboardRouter();

    const { setErrorInfo } = useContext(errorStore);
    const {
        session: { specimen },
    } = useContext(sessionStore);
    const images = useImages();
    const clustersInfo = useMemo<
        (AggregatedInfo | Cluster)[] | undefined
    >(() => {
        return generateClustering(
            clustering,
            classrooms,
            classroomId,
            moduleId,
            intl
        );
    }, [classroomId, classrooms, clustering, intl, moduleId]);

    // clustersInfo[0].id will be GROUPS_MEAN_SCORE_ID if there is more than 1 cluster, or that cluster if not
    const defaultOption = clustersInfo ? clustersInfo[0].id : "";
    const [selectedClusterId, setSelectedClusterId] =
        useState<string>(defaultOption);

    const onClusterSelection = (clusterId: string) => {
        if (clusterId === selectedClusterId) {
            setSelectedClusterId(defaultOption);
        } else {
            setSelectedClusterId(clusterId);
        }
    };

    const location = useLocation<{ studentClusterId: string }>();
    const locationState = location.state;
    useEffect(() => {
        if (locationState) {
            setSelectedClusterId(locationState.studentClusterId);
        } else {
            setSelectedClusterId(defaultOption);
        }
    }, [locationState, moduleId, defaultOption]);

    const currentUrl = window.location.pathname.split("/");
    const backToUrl = getProgressionOverviewUrl(currentUrl[3], currentUrl[4]);

    const { dashboardLoaded } = useDynamicDashboard();

    const history = useHistory();
    useEffect(() => {
        if (!dashboardLoaded) {
            return;
        }
        const classroomAnalytics =
            classrooms === undefined
                ? undefined
                : classrooms.find((classroom) => classroom.id === classroomId);
        if (!classroomAnalytics) {
            setErrorInfo({
                displayModal: true,
                modal: {
                    type: "NOTIFICATION",
                    content: {
                        title: intl.formatMessage(
                            dashboardMessages.classroomNotFound
                        ),
                    },
                },
            });
            history.push(classroomListUrl);
        }
    }, [
        classroomListUrl,
        classroomId,
        classrooms,
        history,
        intl,
        setErrorInfo,
        dashboardLoaded,
    ]);

    const title = intl.formatMessage(messages.groups);
    const classroom =
        classrooms === undefined ||
        classrooms.find === undefined ||
        classrooms.length === 0
            ? ""
            : classrooms.find((classroom) => classroom.id === classroomId);
    if (!classroom) {
        return (
            <>
                <LegacyLoader />
            </>
        );
    }

    const complete_title = `${classroom.name} - ${title}`;

    return (
        <PageWrapper className="clustering">
            <TabTitle title={complete_title} />

            <div>
                <PageHeader
                    title={{ content: title }}
                    className="clustering__header"
                    backTo={backToUrl}
                />
                <ClusteringNav classrooms={classrooms} />
            </div>
            {!clustersInfo && (
                <ContainerBlock className="clustering__block --no-data">
                    <EmptyContainer
                        img={images?.dashboard?.teacher?.clustering?.empty}
                        defaultImg={defaultImages.teacherClusteringEmpty}
                    >
                        <p>{intl.formatMessage(messages.noClusterData)}</p>
                    </EmptyContainer>
                </ContainerBlock>
            )}
            {clustersInfo && (
                <>
                    <ContainerBlock className="clustering__block">
                        <h2>
                            {`${intl.formatMessage(
                                messages.identifiedGroups
                            )} (${clustersInfo.filter(isCluster).length})`}
                        </h2>
                        <ClustersVisualRepresentation
                            clustersInfo={clustersInfo.filter(isCluster)}
                            selectedCluster={selectedClusterId}
                            onClusterSelection={onClusterSelection}
                        />
                    </ContainerBlock>

                    <ContainerBlock className="clustering__block">
                        <div className="clustering__groups-data-container">
                            <ClusterDataTable
                                className="clustering__groups-data"
                                clustersInfo={clustersInfo}
                                selectedClusterId={selectedClusterId}
                                selectCluster={selectCluster(
                                    intl,
                                    clustersInfo,
                                    selectedClusterId,
                                    onClusterSelection
                                )}
                            />
                        </div>
                    </ContainerBlock>
                </>
            )}

            {specimen && (
                <SpecimenHelper
                    dialogTitle={intl.formatMessage(
                        messages.specipenHelperTitle
                    )}
                    dialogDescription={intl.formatMessage(
                        messages.specimenHelperDescription
                    )}
                />
            )}
        </PageWrapper>
    );
};

export default Clustering;

const selectCluster = (
    intl: IntlShape,
    clustersInfo: Cluster[],
    selectedClusterId: string,
    onClusterSelection: (clusterId: string) => void
) => {
    return (
        <Select
            label={intl.formatMessage(messages.selectCluster)}
            className="clustering__select"
            onSelectOption={(value) => {
                const cluster = clustersInfo?.find(
                    (cluster: any) => cluster.id === value
                );
                if (cluster) onClusterSelection(cluster.id);
            }}
            selectedOption={getClusterName(
                selectedClusterId,
                clustersInfo,
                intl,
                messages
            )}
        >
            {clustersInfo &&
                clustersInfo.map((cluster: any) => (
                    <option key={`option-${cluster.id}`} value={cluster.id}>
                        {getClusterName(
                            cluster.id,
                            clustersInfo,
                            intl,
                            messages
                        )}
                    </option>
                ))}
        </Select>
    );
};

const isCluster = (cluster: AggregatedInfo | Cluster) => {
    return cluster.id !== GROUPS_MEAN_SCORE_ID;
};

const messages = defineMessages({
    identifiedGroups: {
        id: "dashboard-classList-identifiedGroups",
        defaultMessage: "Identified groups",
    },
    groupsMeanScore: {
        id: "clustering-groupsMeanScore",
        defaultMessage: "Groups",
    },
    groups: {
        id: "dashboard-common-groups",
        defaultMessage: "Groups",
    },
    selectCluster: {
        id: "dashboard-common-select-cluster",
        defaultMessage: "Select a cluster",
    },
    group: {
        id: "dashboard-common-group",
        defaultMessage: "Group",
    },
    classrooms: {
        id: "dashboard-common-classes",
        defaultMessage: "Classes",
    },
    noClusterData: {
        id: "dashboard-clustering-noClustersForClassAndModudle",
        defaultMessage: "No clusters for this module",
    },
    specipenHelperTitle: {
        id: "specimen-clustering-helper-title",
        defaultMessage: "Groups of students with similar needs",
    },
    specimenHelperDescription: {
        id: "specimen-clustering-helper-description",
        defaultMessage:
            "The clustering algorithm forms groups of students with similar learning characteristics for a given module.",
    },
});
